import { isAxiosError } from "axios"
import { useCallback, useRef, useState } from "react"
import { useMutation } from "@tanstack/react-query"
import { toast } from "react-toastify/unstyled"

import { MessageAttachment } from "../interfaces"

import { uploadChatAttachment } from "./api"
import { AttachmentToastContent } from "./AttachmentToastContent"

const UNSUPPORTED_MIME_TYPE_ERROR = "unsupported_mime_type"
const FILE_TOO_LARGE_ERROR = "file_too_large"
const IMAGE_TOO_LARGE_ERROR = "image_too_large"
const UPLOAD_LIMIT_ERROR = "upload_limit_exceed"

const MAX_ATTACHMENTS_PER_MESSAGE = 4
const UNSUPPORTED_MIME_TYPE_TOAST_TEXT =
    "Формат файла не поддерживается.\n\nПожалуйста, выберите файл одного из допустимых форматов: PDF, DOC/DOCX (.doc,.docx), XLS(X), PPT(X), TXT, JPEG/JPG, PNG."
const FILE_TOO_LARGE_TOAST_TEXT = (fileName: string) =>
    `Не получилось загрузить ${fileName}.\nРазмер файла превышает 10 МБ`
const TOO_MANY_FILES_TOAST_TEXT =
    "Превышено максимальное количество файлов. Можно загрузить не более 4 файлов за раз и не более 10 файлов в час."
const UNKNOWN_UPLOAD_ERROR_TOAST_TEXT = "Что-то пошло не так. Попробуйте еще раз."
const ATTENTION_ICON_SRC = "/images/attention.png"

const showAttachmentToast = (message: string) => {
    toast.error(AttachmentToastContent({ iconSrc: ATTENTION_ICON_SRC, message }))
}

export interface DialogAttachment {
    key: string
    file: File
    isUploading: boolean
    progress?: number
    abortController?: AbortController
    error?: string
    uploadedAttachment?: MessageAttachment
}

interface UploadAttachmentMutationArgs {
    file: File
    onProgress?: (percent: number) => void
    signal?: AbortSignal
}

const getAttachmentKey = (file: File) => `${file.name}-${file.size}-${file.lastModified}`

const createPendingAttachment = (file: File) => ({
    key: getAttachmentKey(file),
    file,
    isUploading: true,
    progress: 0,
    abortController: new AbortController(),
})

const getUploadErrorFlags = (error: unknown) => {
    const isAxios = isAxiosError<{
        errors?: string[]
    }>(error)
    const payloadErrors = isAxios ? (error.response?.data?.errors ?? []) : []
    const transportMessage = (
        error instanceof Error ? error.message : String(error ?? "")
    ).toLowerCase()

    const isPayloadTooLarge =
        payloadErrors.includes(FILE_TOO_LARGE_ERROR) ||
        payloadErrors.includes(IMAGE_TOO_LARGE_ERROR) ||
        transportMessage.includes("413")
    const mimeTypeIsUnsupported = payloadErrors.includes(UNSUPPORTED_MIME_TYPE_ERROR)
    const uploadLimitExceed = payloadErrors.includes(UPLOAD_LIMIT_ERROR)

    return { isPayloadTooLarge, mimeTypeIsUnsupported, uploadLimitExceed }
}

const showUploadErrorToast = (
    attachmentName: string,
    flags: {
        isPayloadTooLarge: boolean
        mimeTypeIsUnsupported: boolean
        uploadLimitExceed: boolean
    }
) => {
    if (flags.isPayloadTooLarge) {
        showAttachmentToast(FILE_TOO_LARGE_TOAST_TEXT(attachmentName))
        return
    }

    if (flags.mimeTypeIsUnsupported) {
        showAttachmentToast(UNSUPPORTED_MIME_TYPE_TOAST_TEXT)
        return
    }

    if (flags.uploadLimitExceed) {
        showAttachmentToast(TOO_MANY_FILES_TOAST_TEXT)
        return
    }

    showAttachmentToast(UNKNOWN_UPLOAD_ERROR_TOAST_TEXT)
}

const getUploadErrorMessage = (flags: {
    isPayloadTooLarge: boolean
    mimeTypeIsUnsupported: boolean
    uploadLimitExceed: boolean
}) => {
    if (flags.isPayloadTooLarge) return "Размер файла превышает 10 МБ"
    if (flags.mimeTypeIsUnsupported) return "Формат файла не поддерживается"
    if (flags.uploadLimitExceed) return "Превышен лимит на количество загрузок в час"
    return "Ошибка загрузки"
}

export function useChatAttachments(token: string | null) {
    const [attachments, setAttachments] = useState<DialogAttachment[]>([])
    const attachmentsRef = useRef<DialogAttachment[]>([])
    const updateAttachments = useCallback(
        (updater: (prev: DialogAttachment[]) => DialogAttachment[]) => {
            const nextAttachments = updater(attachmentsRef.current)
            attachmentsRef.current = nextAttachments
            setAttachments(nextAttachments)
        },
        []
    )
    const uploadAttachmentMutation = useMutation({
        mutationFn: ({ file, onProgress, signal }: UploadAttachmentMutationArgs) =>
            uploadChatAttachment(file, token ?? undefined, onProgress, signal),
    })

    const onSelectFiles = useCallback(
        (files: File[]) => {
            const uploadAttachment = async (attachment: DialogAttachment) => {
                try {
                    const uploadedAttachment = await uploadAttachmentMutation.mutateAsync({
                        file: attachment.file,
                        onProgress: (percent) => {
                            updateAttachments((prev) =>
                                prev.map((existing) =>
                                    existing.key === attachment.key
                                        ? { ...existing, progress: percent }
                                        : existing
                                )
                            )
                        },
                        signal: attachment.abortController?.signal,
                    })

                    updateAttachments((prev) =>
                        prev.map((existing) =>
                            existing.key === attachment.key
                                ? {
                                      ...existing,
                                      isUploading: false,
                                      progress: 100,
                                      abortController: undefined,
                                      uploadedAttachment,
                                  }
                                : existing
                        )
                    )
                } catch (error) {
                    if (isAxiosError(error) && error.code === "ERR_CANCELED") {
                        return
                    }

                    const flags = getUploadErrorFlags(error)
                    showUploadErrorToast(attachment.file.name, flags)

                    updateAttachments((prev) =>
                        prev.map((existing) =>
                            existing.key === attachment.key
                                ? {
                                      ...existing,
                                      isUploading: false,
                                      progress: 0,
                                      abortController: undefined,
                                      error: getUploadErrorMessage(flags),
                                  }
                                : existing
                        )
                    )
                }
            }

            const existingKeys = new Set(attachmentsRef.current.map((attachment) => attachment.key))
            const uniqueAttachments = files
                .map(createPendingAttachment)
                .filter((attachment) => !existingKeys.has(attachment.key))

            if (!uniqueAttachments.length) return

            const availableSlots = Math.max(
                MAX_ATTACHMENTS_PER_MESSAGE - attachmentsRef.current.length,
                0
            )
            if (availableSlots === 0) {
                showAttachmentToast(TOO_MANY_FILES_TOAST_TEXT)
                return
            }

            const pendingUploads = uniqueAttachments.slice(0, availableSlots)
            if (uniqueAttachments.length > pendingUploads.length) {
                showAttachmentToast(TOO_MANY_FILES_TOAST_TEXT)
            }

            const nextAttachments = [...attachmentsRef.current, ...pendingUploads]
            updateAttachments(() => nextAttachments)

            pendingUploads.forEach((attachment) => {
                void uploadAttachment(attachment)
            })
        },
        [updateAttachments, uploadAttachmentMutation]
    )

    const onRemoveAttachment = useCallback(
        (key: string) => {
            const attachment = attachmentsRef.current.find((item) => item.key === key)
            attachment?.abortController?.abort()

            const nextAttachments = attachmentsRef.current.filter((item) => item.key !== key)
            updateAttachments(() => nextAttachments)
        },
        [updateAttachments]
    )

    const clearAttachments = useCallback(() => {
        attachmentsRef.current.forEach((attachment) => attachment.abortController?.abort())
        updateAttachments(() => [])
    }, [updateAttachments])

    return {
        attachments,
        onSelectFiles,
        onRemoveAttachment,
        clearAttachments,
    }
}
